import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, MapPin, User, ShoppingCart, LogOut, Shield, Menu, X, Home, LayoutGrid, ClipboardList, BadgePercent, LogIn, UserPlus, Smartphone } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import CoverageModal from './CoverageModal';
import SearchOverlay from './SearchOverlay';
import { UserAvatar } from './VapeAvatars';
import { useSiteBrand } from './BrandLogo';
import api from '../lib/api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const useHeaderMarquee = () => {
  const [marquee, setMarquee] = useState(DEFAULT_MARQUEE);
  const [marqueeSpeed, setMarqueeSpeed] = useState(40);
  useEffect(() => {
    api.get('/settings').then((r) => {
      const arr = (r.data?.marquee_messages || []).filter((m) => (m || '').trim());
      if (arr.length) setMarquee(arr);
      const spd = Number(r.data?.marquee_speed);
      if (Number.isFinite(spd) && spd > 0) setMarqueeSpeed(spd);
    }).catch(() => {});
  }, []);
  return { marquee, marqueeSpeed };
};

const Logo = ({ compact = false }) => {
  const brand = useSiteBrand();
  const logoUrl = brand.logo_url;
  const rawName = brand.site_name || 'NUBES MTY';
  const parts = rawName.trim().split(/\s+/);
  const primary = parts[0] || 'SITIO';
  const secondary = parts.slice(1).join(' ');
  const configuredHeight = Number(brand.logo_height);
  const height = Number.isFinite(configuredHeight) && configuredHeight > 0
    ? Math.min(compact ? 40 : 96, configuredHeight)
    : (compact ? 32 : 40);

  return (
    <Link to="/" className="flex items-center gap-2 select-none shrink-0" data-testid="site-logo">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={rawName}
          style={{ height: `${height}px` }}
          className="w-auto object-contain"
        />
      ) : (
        <>
          <div className={`${compact ? 'w-8 h-8' : 'w-9 h-9'} rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/25`}>
            <svg viewBox="0 0 24 24" fill="none" className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-white`}>
              <path d="M6 3v6a6 6 0 1 0 12 0V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M12 15v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-white font-black text-base sm:text-lg tracking-tight uppercase">{primary}</span>
            {secondary && (
              <span className="text-orange-500 font-extrabold text-[9px] sm:text-[10px] tracking-[0.32em] sm:tracking-[0.35em] uppercase">{secondary}</span>
            )}
          </div>
        </>
      )}
    </Link>
  );
};

const MobileMenu = ({ user, onLogout, onCoverage, onClose }) => {
  const navigate = useNavigate();
  const go = (path) => { onClose(); navigate(path); };
  return (
    <div className="flex flex-col h-full text-white">
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <Logo compact />
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
          <X className="w-5 h-5" />
        </button>
      </div>

      {user ? (
        <div className="p-5 flex items-center gap-3 border-b border-white/10 cursor-pointer hover:bg-white/5" onClick={() => go('/perfil')}>
          <UserAvatar picture={user.picture} name={user.name} phone={user.phone} email={user.email} size={48} />
          <div className="min-w-0 flex-1">
            <p className="font-bold truncate">{user.name || user.phone || user.email}</p>
            {user.email && <p className="text-white/50 text-xs truncate">{user.email}</p>}
            <p className="text-orange-400 text-[10px] font-bold mt-0.5">Ver mi perfil →</p>
          </div>
        </div>
      ) : (
        <div className="p-4 flex gap-2 border-b border-white/10">
          <button onClick={() => go('/inicio-sesion')} className="flex-1 h-11 rounded-full bg-white text-black hover:bg-white/90 font-bold text-sm inline-flex items-center justify-center gap-1">
            <LogIn className="w-4 h-4" /> Ingresar
          </button>
          <button onClick={() => go('/registro')} className="flex-1 h-11 rounded-full bg-transparent border-2 border-orange-500 text-orange-400 hover:bg-orange-500/10 font-bold text-sm inline-flex items-center justify-center gap-1">
            <UserPlus className="w-4 h-4" /> Registro
          </button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {[
          { to: '/', label: 'Inicio', icon: Home },
          { to: '/catalogo', label: 'Catálogo', icon: LayoutGrid },
          { to: '/promociones', label: 'Ofertas', icon: BadgePercent },
          { to: '/pedidos', label: 'Mis pedidos', icon: ClipboardList },
        ].map((it) => (
          <button
            key={it.to}
            onClick={() => go(it.to)}
            className="w-full h-12 rounded-xl bg-white/5 hover:bg-orange-500/10 hover:text-orange-300 text-white/90 font-semibold px-4 flex items-center gap-3 transition-colors"
          >
            <it.icon className="w-5 h-5" />
            {it.label}
          </button>
        ))}
        <button onClick={() => { onClose(); onCoverage(); }} className="w-full h-12 rounded-xl bg-white/5 hover:bg-orange-500/10 hover:text-orange-300 text-white/90 font-semibold px-4 flex items-center gap-3">
          <MapPin className="w-5 h-5" /> Verificar cobertura
        </button>
        <button onClick={() => go('/como-instalar')} className="w-full h-12 rounded-xl bg-white/5 hover:bg-orange-500/10 hover:text-orange-300 text-white/90 font-semibold px-4 flex items-center gap-3">
          <Smartphone className="w-5 h-5" /> Instalar app
        </button>
        {user?.is_admin && (
          <button onClick={() => go('/admin')} className="w-full h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 text-orange-300 font-bold px-4 flex items-center gap-3 hover:bg-orange-500/20">
            <Shield className="w-5 h-5" /> Panel admin
          </button>
        )}
      </nav>

      {user && (
        <div className="p-4 border-t border-white/10">
          <button onClick={() => { onClose(); onLogout(); }} className="w-full h-11 rounded-full bg-white/5 hover:bg-rose-500/15 text-white/85 hover:text-rose-300 font-semibold text-sm flex items-center justify-center gap-2">
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
};

const DEFAULT_MARQUEE = [
  '🚚 Envío gratis en compras de $1,500',
  '✓ Marcas 100% originales',
  '📦 Envíos con DHL y Pikpak',
  '🔞 Producto exclusivo para adultos 18+',
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [openCoverage, setOpenCoverage] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { marquee, marqueeSpeed } = useHeaderMarquee();
  const { cart } = useCart();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeSearch = (target) => {
    setOpenSearch(false);
    if (typeof target === 'string') navigate(target);
  };

  return (
    <>
      <header className={`sticky top-0 z-40 transition-colors duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-md border-b border-white/5' : 'bg-black border-b border-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-6 h-14 sm:h-16 flex items-center gap-2 md:gap-4">
          {/* Mobile menu button */}
          <Sheet open={openMenu} onOpenChange={setOpenMenu}>
            <SheetTrigger asChild>
              <button className="md:hidden w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center text-white" aria-label="Menú">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-neutral-950 border-white/10 p-0 w-[85%] max-w-xs">
              <MobileMenu
                user={user}
                onLogout={logout}
                onCoverage={() => setOpenCoverage(true)}
                onClose={() => setOpenMenu(false)}
              />
            </SheetContent>
          </Sheet>

          <Logo />
          <div className="hidden md:block h-8 w-px bg-white/10 mx-1" />

          {/* Desktop inline search */}
          <button
            onClick={() => setOpenSearch(true)}
            className="hidden md:flex flex-1 max-w-2xl mx-auto h-10 items-center gap-3 rounded-full bg-white/8 border border-white/10 hover:border-orange-500/40 hover:bg-white/10 text-white/60 hover:text-white/80 px-4"
            aria-label="Abrir buscador"
          >
            <Search className="w-4 h-4 text-orange-500" />
            <span className="text-sm">Encuentra tu vape perfecto 🔥</span>
          </button>

          {/* Mobile search button */}
          <button
            onClick={() => setOpenSearch(true)}
            className="md:hidden ml-auto w-10 h-10 rounded-full bg-white/8 border border-white/10 hover:bg-orange-500/15 hover:border-orange-500/40 flex items-center justify-center text-orange-400"
            aria-label="Buscar"
          >
            <Search className="w-5 h-5" />
          </button>

          {/* Cobertura (desktop) */}
          <button
            onClick={() => setOpenCoverage(true)}
            data-testid="coverage-btn"
            className="hidden lg:inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-white/85 hover:text-white text-sm font-semibold hover:bg-white/10 transition-colors"
          >
            <MapPin className="w-4 h-4 text-orange-400" />
            <span>Cobertura</span>
          </button>

          {/* Cómo instalar la app (desktop) */}
          <button
            onClick={() => navigate('/como-instalar')}
            data-testid="install-app-btn"
            className="hidden lg:inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-white/85 hover:text-white text-sm font-semibold hover:bg-white/10 transition-colors"
          >
            <Smartphone className="w-4 h-4 text-orange-400" />
            <span>Instalar app</span>
          </button>

          {!user ? (
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => navigate('/registro')}
                className="h-9 px-3 rounded-full text-white/85 hover:text-white text-sm font-semibold"
              >
                Crear cuenta
              </button>
              <button
                onClick={() => navigate('/inicio-sesion')}
                className="flex items-center gap-2 h-9 px-4 rounded-full bg-white text-black hover:bg-white/90 font-semibold text-sm"
              >
                <User className="w-4 h-4" /> Ingresar
              </button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:flex items-center gap-2 h-9 pl-1 pr-3 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-semibold">
                  <UserAvatar picture={user.picture} name={user.name} phone={user.phone} email={user.email} size={28} />
                  <span className="max-w-[110px] truncate">{user.name || user.phone || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-neutral-950 text-white border-white/10">
                <DropdownMenuLabel>Mi cuenta</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate('/perfil')}>Mi perfil</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/pedidos')}>Mis pedidos</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/carrito')}>Carrito</DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <Shield className="w-4 h-4 mr-2" /> Panel admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Link to="/carrito" className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10" aria-label="Carrito">
            <ShoppingCart className="w-5 h-5 text-white" />
            {cart.count > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {cart.count}
              </span>
            )}
          </Link>
        </div>

        {/* Marquee strip - real animated scroll (oculto en /admin) */}
        {!isAdminRoute && (
          <div className="bg-orange-500 text-black overflow-hidden">
            <div
              className="marquee flex whitespace-nowrap py-1.5 text-xs md:text-sm font-bold"
              style={{ animationDuration: `${marqueeSpeed}s` }}
            >
              {Array.from({ length: 2 }).map((_, i) => (
                <span key={i} className="inline-flex items-center gap-3 px-6 shrink-0">
                  {marquee.map((msg, j) => (
                    <React.Fragment key={j}>
                      <span>{msg}</span>
                      <span>•</span>
                    </React.Fragment>
                  ))}
                </span>
              ))}
            </div>
          </div>
        )}
      </header>

      <CoverageModal open={openCoverage} onOpenChange={setOpenCoverage} />
      <SearchOverlay open={openSearch} onClose={closeSearch} />
    </>
  );
};

export default Header;
